import { defineStore, storeToRefs } from 'pinia'
import { ref } from 'vue'
import { track, Events } from '@outputinc/o-analytics'
import { useScreenStore } from '@/store/ScreenStore'
import { usePlanStore } from './PlanStore'

export const useHotKeyStore = defineStore('HotKeyStore', () => {
  const active = ref(false)
  const previousKey = ref()
  const screenStore = useScreenStore()
  const planStore = usePlanStore()
  const { currentScreen, hotKeys } = storeToRefs(screenStore)
  const { selectedPlanProduct } = storeToRefs(planStore)

  const keyUpEventListener = (event: KeyboardEvent) => {
    if (active.value) {
      return false
    }

    if (previousKey.value === 'Tab' && event.key === 'Enter') {
      previousKey.value = event.key
      return false
    }
    previousKey.value = event.key

    switch (event.key) {
      case 'Tab':
        return
      case 'Enter':
        return keyPressEnter()
      case 'ArrowUp':
      case 'PageUp':
      case 'ArrowDown':
      case 'PageDown':
        return arrowKeyNavigation(event.key)
    }
    keyPressUpHotkeys(event)
  }

  const keyPressEnter = () => {
    if (active.value) {
      return false
    }

    const element = document.getElementById(`screen-continue-${currentScreen.value.id}`)
    if (element) {
      track(Events.CHECKOUT_SUBSCRIPTION_UI_ENTER, { screen: currentScreen.value.id, products: [selectedPlanProduct.value] })
      element.click()
    }
  }

  const arrowKeyNavigation = (key: KeyboardEvent['key']) => {
    const direction = key === 'ArrowUp' || key === 'PageUp' ? 'Previous' : 'Next'
    const top = direction === 'Previous' ? -150 : 150
    const { navigateNext, navigatePrevious } = useScreenStore()

    const content = document.getElementById('checkout-content')
    const contentInner = document.getElementById('checkout-content-inner')

    if (!content || !contentInner) {
      return
    }

    if (content.offsetHeight < contentInner.offsetHeight) {
      if (!document.activeElement?.isSameNode(content)) {
        content?.scrollBy({
          top,
          left: 0,
          behavior: 'smooth'
        })
      }
      // Screen is scrollable
      if (direction === 'Previous' && Math.round(contentInner.getBoundingClientRect().top) === Math.round(content.getBoundingClientRect().top)) {
        // At top of screen
        navigatePrevious()
      }
      if (direction === 'Next' && Math.round(contentInner.getBoundingClientRect().bottom) === Math.round(content.getBoundingClientRect().bottom)) {
        // At bottom of screen
        navigateNext()
      }
    } else {
      // Screen is not scrollable
      return direction === 'Previous' ? navigatePrevious() : navigateNext()
    }
  }

  const keyPressUpHotkeys = (e: KeyboardEvent) => {
    // Don't fire hotkeys while the user is typing in a form input — the keystroke belongs to the field.
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

    const normalizedKey = e.key.toLowerCase()
    const key = hotKeys.value.find((k: string) => k === normalizedKey)

    const hotKey: HTMLButtonElement | null = document.querySelector(`[data-hotkey=${key}]`)

    if (hotKey) {
      hotKey.classList.remove('o-btn-pressed')
      hotKey.click()
      trackHotkeyPress(hotKey)
    }
  }

  const trackHotkeyPress = (hotKey: HTMLButtonElement) => {
    if (hotKey && hotKey.dataset.hotkey) {
      const eventProperties = {
        key: hotKey.dataset.hotkey.toLowerCase(),
        text: hotKey.dataset.text,
        value: hotKey.value,
        screen: currentScreen.value.id,
        products: [selectedPlanProduct.value]
      }
      track(Events.CHECKOUT_SUBSCRIPTION_UI_HOTKEY, eventProperties)
    }
  }

  return {
    keyUpEventListener
  }
})
